/**
 * Floating Link Editor Plugin
 *
 * Based on Lexical Playground's FloatingLinkEditorPlugin with custom UI enhancement.
 * Core logic handles link detection, selection tracking, and positioning.
 * UI rendering is delegated to FloatingLinkEditorModal component for separation of concerns.
 */

import React, { Dispatch, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { $createLinkNode, $isAutoLinkNode, $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $findMatchingParent, mergeRegister } from '@lexical/utils';
import {
  $createTextNode,
  $getSelection,
  $isLineBreakNode,
  $isNodeSelection,
  $isRangeSelection,
  BaseSelection,
  CLICK_COMMAND,
  COMMAND_PRIORITY_CRITICAL,
  COMMAND_PRIORITY_HIGH,
  COMMAND_PRIORITY_LOW,
  getDOMSelection,
  KEY_ESCAPE_COMMAND,
  LexicalEditor,
  SELECTION_CHANGE_COMMAND,
} from 'lexical';

import { getSelectedNode } from '../../../utils/getSelectedNode';
import { setFloatingElemPositionForLinkEditor } from '../../../utils/setFloatingElemPositionForLinkEditor';
import { sanitizeUrl } from '../../../utils/url';
import { FloatingLinkEditorModal } from '../../../ui/FloatingLinkEditorModal';

function FloatingLinkEditor({
  editor,
  isLink,
  setIsLink,
  anchorElem,
  isLinkEditMode,
  setIsLinkEditMode,
  isNewLink,
  setIsNewLink,
}: {
  editor: LexicalEditor;
  isLink: boolean;
  setIsLink: Dispatch<boolean>;
  anchorElem: HTMLElement;
  isLinkEditMode: boolean;
  setIsLinkEditMode: Dispatch<boolean>;
  isNewLink: boolean;
  setIsNewLink: Dispatch<boolean>;
}): JSX.Element {
  const editorRef = useRef<HTMLDivElement | null>(null);
  const urlInputRef = useRef<HTMLInputElement>(null);
  const textInputRef = useRef<HTMLInputElement>(null);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const [editedLinkUrl, setEditedLinkUrl] = useState('https://');
  const [editedLinkText, setEditedLinkText] = useState('');
  const [lastSelection, setLastSelection] = useState<BaseSelection | null>(null);

  const updateLinkEditor = useCallback(() => {
    const selection = $getSelection();

    if ($isRangeSelection(selection)) {
      const node = getSelectedNode(selection);
      const linkParent = $findMatchingParent(node, $isLinkNode);

      let currentUrl = '';
      let currentText = '';

      if (linkParent) {
        currentUrl = linkParent.getURL();
        currentText = linkParent.getTextContent();
      } else if ($isLinkNode(node)) {
        currentUrl = node.getURL();
        currentText = node.getTextContent();
      }

      setLinkUrl(currentUrl);
      setLinkText(currentText);

      if (isLinkEditMode) {
        setEditedLinkUrl(currentUrl);
        setEditedLinkText(currentText);
      }
    } else if ($isNodeSelection(selection)) {
      const nodes = selection.getNodes();
      if (nodes.length > 0) {
        const node = nodes[0];
        const parent = node.getParent();
        let currentUrl = '';
        let currentText = '';

        if ($isLinkNode(parent)) {
          currentUrl = parent.getURL();
          currentText = parent.getTextContent();
        } else if ($isLinkNode(node)) {
          currentUrl = node.getURL();
          currentText = node.getTextContent();
        }

        setLinkUrl(currentUrl);
        setLinkText(currentText);

        if (isLinkEditMode) {
          setEditedLinkUrl(currentUrl);
          setEditedLinkText(currentText);
        }
      }
    }

    const editorElem = editorRef.current;
    const nativeSelection = getDOMSelection(editor._window);
    const activeElement = document.activeElement;

    if (editorElem === null) {
      return;
    }

    const rootElement = editor.getRootElement();

    if (selection !== null && rootElement !== null && editor.isEditable()) {
      let domRect: DOMRect | undefined;

      if ($isNodeSelection(selection)) {
        const nodes = selection.getNodes();
        if (nodes.length > 0) {
          const element = editor.getElementByKey(nodes[0].getKey());
          if (element) {
            domRect = element.getBoundingClientRect();
          }
        }
      } else if (
        nativeSelection !== null &&
        rootElement.contains(nativeSelection.anchorNode)
      ) {
        domRect = nativeSelection.focusNode?.parentElement?.getBoundingClientRect();
      }

      if (domRect) {
        domRect.y += 40;
        setFloatingElemPositionForLinkEditor(domRect, editorElem, anchorElem);
      }
      setLastSelection(selection);
    } else if (!activeElement || activeElement.className !== 'link-input') {
      if (rootElement !== null) {
        setFloatingElemPositionForLinkEditor(null, editorElem, anchorElem);
      }
      setLastSelection(null);
      setIsLinkEditMode(false);
      setLinkUrl('');
      setLinkText('');
    }

    return true;
  }, [anchorElem, editor, setIsLinkEditMode, isLinkEditMode]);

  useEffect(() => {
    const scrollerElem = anchorElem.parentElement;

    const update = () => {
      editor.getEditorState().read(() => {
        updateLinkEditor();
      });
    };

    window.addEventListener('resize', update);
    if (scrollerElem) {
      scrollerElem.addEventListener('scroll', update);
    }

    return () => {
      window.removeEventListener('resize', update);
      if (scrollerElem) {
        scrollerElem.removeEventListener('scroll', update);
      }
    };
  }, [anchorElem, editor, updateLinkEditor]);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateLinkEditor();
        });
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          updateLinkEditor();
          return true;
        },
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand(
        KEY_ESCAPE_COMMAND,
        () => {
          if (isLink) {
            setIsLink(false);
            return true;
          }
          return false;
        },
        COMMAND_PRIORITY_HIGH
      )
    );
  }, [editor, updateLinkEditor, setIsLink, isLink]);

  useEffect(() => {
    editor.getEditorState().read(() => {
      updateLinkEditor();
    });
  }, [editor, updateLinkEditor]);

  useEffect(() => {
    if (isLinkEditMode && urlInputRef.current) {
      urlInputRef.current.focus();
    }
  }, [isLinkEditMode, isLink]);

  useEffect(() => {
    const editorElement = editorRef.current;
    if (editorElement === null) {
      return;
    }
    const handleBlur = (event: FocusEvent) => {
      // Don't close if we're in edit mode or if focus is moving within the editor element
      if (isLinkEditMode) {
        return;
      }
      if (!editorElement.contains(event.relatedTarget as Element) && isLink) {
        setIsLink(false);
        setIsLinkEditMode(false);
      }
    };
    editorElement.addEventListener('focusout', handleBlur);
    return () => {
      editorElement.removeEventListener('focusout', handleBlur);
    };
  }, [editorRef, setIsLink, setIsLinkEditMode, isLink, isLinkEditMode]);

  const handleLinkSubmission = () => {
    if (lastSelection !== null) {
      const sanitizedUrl = sanitizeUrl(editedLinkUrl);

      if (sanitizedUrl.trim() !== '') {
        editor.update(() => {
          const selection = $getSelection();
          if (!$isRangeSelection(selection)) return;

          const node = getSelectedNode(selection);
          const linkParent = $findMatchingParent(node, $isLinkNode);

          if (linkParent) {
            linkParent.setURL(sanitizedUrl);

            const linkTextNode = linkParent.getFirstChild();
            if (linkTextNode) {
              if (linkTextNode.getTextContent() !== editedLinkText) {
                linkTextNode.replace($createTextNode(editedLinkText));
              }
            } else {
              linkParent.append($createTextNode(editedLinkText));
            }
          } else {
            const newLinkNode = $createLinkNode(sanitizedUrl);
            newLinkNode.append($createTextNode(editedLinkText));
            selection.insertNodes([newLinkNode]);
          }

          // Convert AutoLinkNode to LinkNode to preserve attributes
          const parent = getSelectedNode(selection).getParent();
          if ($isAutoLinkNode(parent)) {
            const linkNode = $createLinkNode(parent.getURL(), {
              rel: parent.__rel,
              target: parent.__target,
              title: parent.__title,
            });
            parent.replace(linkNode, true);
          }
        });
      } else {
        editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
      }

      setEditedLinkUrl('https://');
      setEditedLinkText('');
      setIsLinkEditMode(false);
      setIsNewLink(false);
    }
  };

  const handleCancel = () => {
    if (isNewLink) {
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          const node = getSelectedNode(selection);
          const linkParent = $findMatchingParent(node, $isLinkNode);
          if (linkParent) {
            const linkText = linkParent.getTextContent();
            if (linkText === 'New Link') {
              linkParent.remove();
            } else {
              const newTextNode = $createTextNode(linkText);
              linkParent.remove();
              selection.insertNodes([newTextNode]);
            }
          }
        }
      });
    }
    setIsNewLink(false);
    setIsLinkEditMode(false);
  };

  const handleEdit = () => {
    setEditedLinkUrl(linkUrl);
    setEditedLinkText(linkText);
    setIsLinkEditMode(true);
  };

  const handleRemove = () => {
    editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
  };

  return createPortal(
    <FloatingLinkEditorModal
      editorRef={editorRef}
      isLink={isLink}
      isLinkEditMode={isLinkEditMode}
      linkUrl={linkUrl}
      linkText={linkText}
      editedLinkUrl={editedLinkUrl}
      editedLinkText={editedLinkText}
      urlInputRef={urlInputRef}
      textInputRef={textInputRef}
      onEditedLinkUrlChange={setEditedLinkUrl}
      onEditedLinkTextChange={setEditedLinkText}
      onSubmit={handleLinkSubmission}
      onCancel={handleCancel}
      onEdit={handleEdit}
      onRemove={handleRemove}
    />,
    anchorElem
  );
}

function useFloatingLinkEditorToolbar(
  editor: LexicalEditor,
  anchorElem: HTMLElement,
  isLinkEditMode: boolean,
  setIsLinkEditMode: Dispatch<boolean>,
  isNewLink: boolean,
  setIsNewLink: Dispatch<boolean>,
): JSX.Element | null {
  const [activeEditor, setActiveEditor] = useState(editor);
  const [isLink, setIsLink] = useState(false);

  useEffect(() => {
    function $updateToolbar() {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        const focusNode = getSelectedNode(selection);
        const focusLinkNode = $findMatchingParent(focusNode, $isLinkNode);
        const focusAutoLinkNode = $findMatchingParent(focusNode, $isAutoLinkNode);
        if (!(focusLinkNode || focusAutoLinkNode)) {
          setIsLink(false);
          return;
        }
        const badNode = selection
          .getNodes()
          .filter((node) => !$isLineBreakNode(node))
          .find((node) => {
            const linkNode = $findMatchingParent(node, $isLinkNode);
            const autoLinkNode = $findMatchingParent(node, $isAutoLinkNode);
            return (
              (focusLinkNode && !focusLinkNode.is(linkNode)) ||
              (linkNode && !linkNode.is(focusLinkNode)) ||
              (focusAutoLinkNode && !focusAutoLinkNode.is(autoLinkNode)) ||
              (autoLinkNode &&
                (!autoLinkNode.is(focusAutoLinkNode) || autoLinkNode.getIsUnlinked()))
            );
          });
        if (!badNode) {
          setIsLink(true);
        } else {
          setIsLink(false);
        }
      } else if ($isNodeSelection(selection)) {
        const nodes = selection.getNodes();
        if (nodes.length === 0) {
          setIsLink(false);
          return;
        }
        const node = nodes[0];
        const parent = node.getParent();
        if ($isLinkNode(parent) || $isLinkNode(node)) {
          setIsLink(true);
        } else {
          setIsLink(false);
        }
      }
    }
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          $updateToolbar();
        });
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        (_payload, newEditor) => {
          $updateToolbar();
          setActiveEditor(newEditor);
          return false;
        },
        COMMAND_PRIORITY_CRITICAL
      ),
      editor.registerCommand(
        CLICK_COMMAND,
        (payload) => {
          const selection = $getSelection();
          if ($isRangeSelection(selection)) {
            const node = getSelectedNode(selection);
            const linkNode = $findMatchingParent(node, $isLinkNode);
            if ($isLinkNode(linkNode) && (payload.metaKey || payload.ctrlKey)) {
              window.open(linkNode.getURL(), '_blank');
              return true;
            }
          }
          return false;
        },
        COMMAND_PRIORITY_LOW
      )
    );
  }, [editor]);

  return createPortal(
    <FloatingLinkEditor
      editor={activeEditor}
      isLink={isLink}
      anchorElem={anchorElem}
      setIsLink={setIsLink}
      isLinkEditMode={isLinkEditMode}
      setIsLinkEditMode={setIsLinkEditMode}
      isNewLink={isNewLink}
      setIsNewLink={setIsNewLink}
    />,
    anchorElem
  );
}

export default function FloatingLinkEditorPlugin({
  anchorElem = document.body,
  isLinkEditMode,
  setIsLinkEditMode,
  isNewLink,
  setIsNewLink,
}: {
  anchorElem?: HTMLElement;
  isLinkEditMode: boolean;
  setIsLinkEditMode: Dispatch<boolean>;
  isNewLink: boolean;
  setIsNewLink: Dispatch<boolean>;
}): JSX.Element | null {
  const [editor] = useLexicalComposerContext();
  return useFloatingLinkEditorToolbar(editor, anchorElem, isLinkEditMode, setIsLinkEditMode, isNewLink, setIsNewLink);
}
